import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ThemeConfig } from '@ext-bookmark/shared/types';
import { defaultThemeConfig } from '../lib/theme';
import { getAppData, saveAppData } from '../lib/storage';

interface AppState {
  // 主题相关
  themeConfig: ThemeConfig;
  /** 仅从存储加载时同步到 store，不写回 storage，避免触发 syncService 动态导入 */
  setThemeConfigFromStorage: (config: ThemeConfig) => void;
  updateThemeConfig: (updates: Partial<ThemeConfig>) => Promise<void>;
  setThemeMode: (mode: 'light' | 'dark') => Promise<void>;
  resetThemeConfig: () => Promise<void>;
  
  // 侧边栏相关
  sidebarWidth: number;
  setSidebarWidth: (width: number) => void;
  
  // UI 状态
  showSettings: boolean;
  setShowSettings: (show: boolean) => void;
  showAuthModal: boolean;
  setShowAuthModal: (show: boolean) => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      themeConfig: defaultThemeConfig,

      setThemeConfigFromStorage: (config) => {
        set({ themeConfig: config });
      },

      updateThemeConfig: async (updates) => {
        const currentConfig = get().themeConfig;
        const newConfig: ThemeConfig = {
          ...currentConfig,
          ...updates,
          // 如果更新了 sidebar，需要合并
          sidebar: updates.sidebar 
            ? { ...currentConfig.sidebar, ...updates.sidebar }
            : currentConfig.sidebar,
        };
        
        // 保存到存储
        try {
          const data = await getAppData();
          data.theme = newConfig.mode;
          data.themeConfig = newConfig;
          await saveAppData(data);
        } catch (error) {
          console.error('Failed to save theme config:', error);
        }
        
        // 应用主题到 DOM
        if (updates.mode !== undefined || newConfig.mode !== currentConfig.mode) {
          document.documentElement.classList.toggle('dark', newConfig.mode === 'dark');
        }
        
        set({ themeConfig: newConfig });
      },
      
      setThemeMode: async (mode) => {
        await get().updateThemeConfig({ mode });
      },
      
      resetThemeConfig: async () => {
        await get().updateThemeConfig(defaultThemeConfig);
      },
      
      sidebarWidth: 280,
      setSidebarWidth: (width) => set({ sidebarWidth: width }),
      
      showSettings: false,
      setShowSettings: (show) => set({ showSettings: show }),
      
      showAuthModal: false,
      setShowAuthModal: (show) => set({ showAuthModal: show }),
    }),
    {
      name: 'app-storage',
      partialize: (state) => ({ 
        themeConfig: state.themeConfig,
        sidebarWidth: state.sidebarWidth,
      }),
    }
  )
);
